import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Button,
  Box,
  Typography,
  CircularProgress,
  Collapse,
  IconButton,
} from "@material-ui/core";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import SubdirectoryArrowRightIcon from "@material-ui/icons/SubdirectoryArrowRight";
import VerticalAlignTopIcon from "@material-ui/icons/VerticalAlignTop";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import axios from "axios";
import envConfig from "../../../bin/env.config";
import { useSelector } from "react-redux";
import { buildProductTree } from "../productTreeBuild";
import { fetchChildrenForParent } from "../projectProductsTreeApi";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

function deepCloneTreeRoots(roots) {
  return roots.map((n) => ({
    product: { ...n.product },
    children: deepCloneTreeRoots(n.children),
  }));
}

function reorderArrayImmutable(arr, fromIndex, toIndex) {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return arr;
  const next = [...arr];
  const [removed] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, removed);
  return next;
}

function findAndReorderSibling(roots, parentId, fromIndex, toIndex) {
  if (parentId == null) {
    return reorderArrayImmutable(roots, fromIndex, toIndex);
  }
  return roots.map((n) => {
    if (n.product.id === parentId) {
      return {
        ...n,
        children: reorderArrayImmutable(n.children, fromIndex, toIndex),
      };
    }
    if (n.children.length) {
      return {
        ...n,
        children: findAndReorderSibling(n.children, parentId, fromIndex, toIndex),
      };
    }
    return n;
  });
}

/**
 * @param {any} node
 * @param {number} id
 */
function subtreeContainsId(node, id) {
  if (node.product.id === id) return true;
  return node.children.some((c) => subtreeContainsId(c, id));
}

/**
 * @param {any[]} roots
 * @param {number} id
 * @returns {{ roots: any[], node: any | null }}
 */
function removeById(roots, id) {
  for (let i = 0; i < roots.length; i++) {
    if (roots[i].product.id === id) {
      return {
        roots: [...roots.slice(0, i), ...roots.slice(i + 1)],
        node: roots[i],
      };
    }
    if (roots[i].children.length) {
      const res = removeById(roots[i].children, id);
      if (res.node) {
        return {
          roots: roots.map((n, j) =>
            j === i ? { ...n, children: res.roots } : n
          ),
          node: res.node,
        };
      }
    }
  }
  return { roots, node: null };
}

/**
 * @param {any[]} roots
 * @param {number | null} parentId
 * @param {number} index
 * @param {any} node
 */
function insertAt(roots, parentId, index, node) {
  if (parentId == null) {
    const r = [...roots];
    const safe = Math.max(0, Math.min(index, r.length));
    r.splice(safe, 0, node);
    return r;
  }
  return roots.map((n) => {
    if (n.product.id === parentId) {
      const ch = [...n.children];
      const safe = Math.max(0, Math.min(index, ch.length));
      ch.splice(safe, 0, node);
      return { ...n, children: ch };
    }
    if (n.children.length) {
      return { ...n, children: insertAt(n.children, parentId, index, node) };
    }
    return n;
  });
}

/**
 * @param {any[]} roots
 * @param {number} id
 * @returns {any | null}
 */
function findById(roots, id) {
  for (const n of roots) {
    if (n.product.id === id) return n;
    const f = findById(n.children, id);
    if (f) return f;
  }
  return null;
}

/**
 * @param {any[]} roots
 * @param {number} draggedId
 * @param {number | null} fromParentId
 * @param {number} fromIndex
 * @param {number | null} toParentId
 * @param {number} toIndex
 */
function moveInTree(roots, draggedId, fromParentId, fromIndex, toParentId, toIndex) {
  if (fromParentId === toParentId && fromIndex === toIndex) {
    return roots;
  }
  if (fromParentId === toParentId) {
    return findAndReorderSibling(roots, fromParentId, fromIndex, toIndex);
  }
  const { roots: r1, node } = removeById(roots, draggedId);
  if (!node) return roots;
  if (toParentId != null && subtreeContainsId(node, toParentId)) {
    return roots;
  }
  return insertAt(r1, toParentId, toIndex, node);
}

/**
 * @param {any[]} roots
 * @param {number} draggedId
 * @param {number} newParentId
 */
function appendAsChild(roots, draggedId, newParentId) {
  const { roots: r1, node } = removeById(roots, draggedId);
  if (!node) return roots;
  if (subtreeContainsId(node, newParentId)) {
    return roots;
  }
  const parentN = findById(r1, newParentId);
  if (!parentN) return roots;
  return insertAt(r1, newParentId, parentN.children.length, node);
}

/**
 * @param {any[]} roots
 * @param {number} draggedId
 */
function moveToRoot(roots, draggedId) {
  const { roots: r1, node } = removeById(roots, draggedId);
  if (!node) return roots;
  return insertAt(r1, null, r1.length, node);
}

/**
 * @param {any[]} roots
 * @returns {Array<{ product: object, sort_order: number, parent_id: number | null }>}
 */
function collectTreePersistencePayload(roots) {
  const updates = [];
  const walk = (list, parentId) => {
    list.forEach((node, i) => {
      updates.push({
        product: node.product,
        sort_order: i,
        parent_id: parentId,
      });
      walk(node.children, node.product.id);
    });
  };
  walk(roots, null);
  return updates;
}

/**
 * @param {any[]} roots
 * @param {number} parentId
 * @param {any[]} newChildren
 */
function updateNodeChildren(roots, parentId, newChildren) {
  return roots.map((n) => {
    if (n.product.id === parentId) {
      return { ...n, children: newChildren };
    }
    if (n.children.length) {
      return { ...n, children: updateNodeChildren(n.children, parentId, newChildren) };
    }
    return n;
  });
}

const nestZoneStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 40,
  padding: 4,
  borderLeft: "1px solid rgba(0,0,0,0.08)",
  cursor: "copy",
  flexShrink: 0,
};

const rootDropStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 12px",
  marginBottom: 8,
  border: "1px dashed #aaa",
  borderRadius: 4,
  backgroundColor: "rgba(0,0,0,0.02)",
  fontSize: "0.85rem",
};

function TreeOrderRows({
  depth,
  parentId,
  nodes,
  onDragStart,
  onDragOver,
  onDropMain,
  onDropNest,
  dragActive,
  onDragEnd,
  eager = true,
  expanded = {},
  onToggleNodeExpand,
  emptyByParent = {},
  loadingByParent = {},
}) {
  return (
    <>
      {nodes.map((node, index) => {
        const pid = node.product.id;
        const pKey = String(pid);
        const isEmpty = emptyByParent[pKey] === true;
        const canExpand = !eager && !isEmpty;
        const isOpen = expanded[pid] === true;
        const isLoading = loadingByParent[pKey] === true;
        const showNestedEager = eager && node.children.length > 0;
        return (
          <React.Fragment key={node.product.id}>
            <Box
              display="flex"
              alignItems="stretch"
              pl={Math.min(2 + depth * 2, 16)}
              style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}
            >
              {eager ? null : canExpand || isOpen ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width={40}
                  flexShrink={0}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleNodeExpand && onToggleNodeExpand(pid);
                    }}
                    disabled={isLoading}
                    aria-label={isOpen ? "Collapse" : "Expand"}
                  >
                    {isLoading ? (
                      <CircularProgress size={20} color="primary" />
                    ) : isOpen ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                </Box>
              ) : (
                <Box width={40} height={40} flexShrink={0} />
              )}
              <Box
                display="flex"
                alignItems="center"
                flex={1}
                minWidth={0}
                py={0.5}
                draggable
                onDragStart={(e) =>
                  onDragStart(e, parentId, index, node.product.id)
                }
                onDragEnd={onDragEnd}
                onDragOver={onDragOver}
                onDrop={(e) => onDropMain(e, parentId, index)}
                style={{ cursor: "grab" }}
                title="Drop on this row: move to this list position (reorder or change level)"
              >
                <DragIndicatorIcon
                  color="action"
                  fontSize="small"
                  style={{ marginRight: 4, flexShrink: 0 }}
                />
                <Box minWidth={0} flex={1}>
                  <Typography variant="body2" noWrap>
                    {node.product.name || "(no name)"}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    #{node.product.id}
                  </Typography>
                </Box>
              </Box>
              <Box
                onDragOver={onDragOver}
                onDrop={(e) => onDropNest(e, node.product.id)}
                style={{
                  ...nestZoneStyle,
                  backgroundColor: dragActive
                    ? "rgba(25, 118, 210, 0.08)"
                    : undefined,
                }}
                title="Drop here: make the dragged item a child of this product"
              >
                <SubdirectoryArrowRightIcon
                  color="action"
                  fontSize="small"
                  style={{ display: "block" }}
                />
              </Box>
            </Box>
            {eager && showNestedEager && (
              <TreeOrderRows
                depth={depth + 1}
                parentId={node.product.id}
                nodes={node.children}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDropMain={onDropMain}
                onDropNest={onDropNest}
                dragActive={dragActive}
                onDragEnd={onDragEnd}
                eager
              />
            )}
            {!eager && (
              <Collapse
                in={isOpen && node.children.length > 0}
                timeout="auto"
                unmountOnExit
              >
                <TreeOrderRows
                  depth={depth + 1}
                  parentId={node.product.id}
                  nodes={node.children}
                  onDragStart={onDragStart}
                  onDragOver={onDragOver}
                  onDropMain={onDropMain}
                  onDropNest={onDropNest}
                  dragActive={dragActive}
                  onDragEnd={onDragEnd}
                  eager={false}
                  expanded={expanded}
                  onToggleNodeExpand={onToggleNodeExpand}
                  emptyByParent={emptyByParent}
                  loadingByParent={loadingByParent}
                />
              </Collapse>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {object[]|null} props.flatListOverride — if non-null, eager full tree (e.g. search). If null, lazy by parent.
 * @param {string|number} props.projectId
 * @param {string} [props.orderBy]
 * @param {string} [props.order]
 * @param {boolean} [props.notMappedYet]
 * @param {number|string} [props.searchIsHidden]
 */
export default function DialogUpdateTreeOrder({
  open,
  onClose,
  flatListOverride,
  projectId,
  orderBy = "name",
  order = "asc",
  notMappedYet = false,
  searchIsHidden = 2,
}) {
  const token = useSelector((state) => state.session.token);
  const isEager = flatListOverride != null;
  const [treeRoots, setTreeRoots] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [expandedD, setExpandedD] = useState({});
  const [emptyByParent, setEmptyByParent] = useState({});
  const [loadingByParent, setLoadingByParent] = useState({});
  const [loadedParents, setLoadedParents] = useState({});
  const [rootLoading, setRootLoading] = useState(false);
  const dragParentIdRef = useRef(null);
  const dragIndexRef = useRef(0);
  const dragIdRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setExpandedD({});
    setEmptyByParent({});
    setLoadingByParent({});
    setLoadedParents({});
    if (isEager) {
      if (flatListOverride && flatListOverride.length) {
        const { roots } = buildProductTree(flatListOverride);
        setTreeRoots(deepCloneTreeRoots(roots));
      } else {
        setTreeRoots([]);
      }
      return;
    }
    let cancelled = false;
    (async () => {
      setRootLoading(true);
      try {
        const list = await fetchChildrenForParent({
          projectId,
          token,
          parentId: null,
          orderBy,
          order,
          searchText: "",
          notMappedYet,
          searchIsHidden,
        });
        if (!cancelled) {
          setTreeRoots(
            list.map((p) => ({ product: p, children: [] }))
          );
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setTreeRoots([]);
        }
      } finally {
        if (!cancelled) {
          setRootLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    isEager,
    flatListOverride,
    projectId,
    token,
    orderBy,
    order,
    notMappedYet,
    searchIsHidden,
  ]);

  const handleToggleNodeExpand = useCallback(
    async (id) => {
      if (isEager) return;
      if (expandedD[id]) {
        setExpandedD((p) => ({ ...p, [id]: false }));
        return;
      }
      const pKey = String(id);
      if (emptyByParent[pKey]) {
        return;
      }
      const n = findById(treeRoots, id);
      if (loadedParents[pKey] || (n && n.children.length > 0)) {
        if (!loadedParents[pKey] && n && n.children.length > 0) {
          setLoadedParents((l) => ({ ...l, [pKey]: true }));
        }
        setExpandedD((prev) => ({ ...prev, [id]: true }));
        return;
      }
      setLoadingByParent((s) => ({ ...s, [pKey]: true }));
      try {
        const list = await fetchChildrenForParent({
          projectId,
          token,
          parentId: id,
          orderBy,
          order,
          searchText: "",
          notMappedYet,
          searchIsHidden,
        });
        setTreeRoots((roots) =>
          updateNodeChildren(
            roots,
            id,
            list.map((p) => ({ product: p, children: [] }))
          )
        );
        setLoadedParents((l) => ({ ...l, [pKey]: true }));
        if (list.length === 0) {
          setEmptyByParent((e) => ({ ...e, [pKey]: true }));
        } else {
          setExpandedD((p) => ({ ...p, [id]: true }));
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingByParent((s) => {
          const n = { ...s };
          delete n[pKey];
          return n;
        });
      }
    },
    [
      isEager,
      expandedD,
      emptyByParent,
      loadedParents,
      treeRoots,
      projectId,
      token,
      orderBy,
      order,
      notMappedYet,
      searchIsHidden,
    ]
  );

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const onDragStart = (e, pId, index, nodeId) => {
    dragParentIdRef.current = pId;
    dragIndexRef.current = index;
    dragIdRef.current = nodeId;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(nodeId));
    setDragActive(true);
  };

  const onDragEnd = useCallback(() => {
    setDragActive(false);
  }, []);

  const onDropMain = useCallback(
    (e, targetParentId, targetIndex) => {
      e.preventDefault();
      e.stopPropagation();
      const fromParentId = dragParentIdRef.current;
      const fromIndex = dragIndexRef.current;
      const id = dragIdRef.current;
      if (id == null) return;
      setTreeRoots((prev) =>
        moveInTree(prev, id, fromParentId, fromIndex, targetParentId, targetIndex)
      );
    },
    [setTreeRoots]
  );

  const onDropNest = useCallback(
    (e, newParentId) => {
      e.preventDefault();
      e.stopPropagation();
      const id = dragIdRef.current;
      if (id == null || newParentId === id) return;
      setTreeRoots((prev) => appendAsChild(prev, id, newParentId));
    },
    [setTreeRoots]
  );

  const onDropRoot = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const id = dragIdRef.current;
      if (id == null) return;
      setTreeRoots((prev) => moveToRoot(prev, id));
    },
    [setTreeRoots]
  );

  const handleSave = async () => {
    if (!projectId || !token) return;
    const updates = collectTreePersistencePayload(treeRoots);
    setSaving(true);
    try {
      const url = `${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/update-product`;
      await Promise.all(
        updates.map(({ product, sort_order, parent_id }) => {
          const putData = {
            ...product,
            sort_order,
            parent_id: parent_id == null ? null : parent_id,
            project_id: projectId,
          };
          return axios.post(url, putData, {
            headers: {
              Authorization: JSON.stringify(token),
            },
          });
        })
      );
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="md"
      aria-labelledby="update-tree-title"
    >
      <DialogTitle id="update-tree-title">Update tree</DialogTitle>
      <DialogContent style={{ minHeight: 200 }}>
        <Typography variant="body2" color="textSecondary" paragraph>
          Drag a row to another row to place it in that list position (sibling
          order, or a different parent). Use the right icon column to make the
          item a <strong>child</strong> of that row. Use the top strip to move an
          item to a <strong>root</strong> (top level). Save to persist; the left
          tree then refreshes.
          {!isEager && (
            <>
              {" "}
              Expand branches (chevron) to load one level at a time from the
              server.
            </>
          )}
        </Typography>
        {rootLoading && !isEager ? (
          <Box py={2} display="flex" justifyContent="center">
            <CircularProgress size={28} />
          </Box>
        ) : treeRoots.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No products to display.
          </Typography>
        ) : (
          <Box
            maxHeight="60vh"
            overflow="auto"
            border="1px solid #e0e0e0"
            borderRadius={4}
            p={1}
          >
            <Box
              style={rootDropStyle}
              onDragOver={onDragOver}
              onDrop={onDropRoot}
            >
              <VerticalAlignTopIcon color="action" fontSize="small" />
              <Typography variant="body2" color="textSecondary" component="span">
                Drop here to set as <strong>root</strong> (appended at the end
                of top level)
              </Typography>
            </Box>
            <TreeOrderRows
              depth={0}
              parentId={null}
              nodes={treeRoots}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDropMain={onDropMain}
              onDropNest={onDropNest}
              dragActive={dragActive}
              onDragEnd={onDragEnd}
              eager={isEager}
              expanded={expandedD}
              onToggleNodeExpand={handleToggleNodeExpand}
              emptyByParent={emptyByParent}
              loadingByParent={loadingByParent}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {saving && <CircularProgress size={20} style={{ marginRight: 8 }} />}
        <Button
          onClick={handleSave}
          color="primary"
          disabled={saving || treeRoots.length === 0 || (!isEager && rootLoading)}
        >
          Save
        </Button>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
